<template>
  <ModalDefault
    id="modal-configuracion-pacientes"
    title="Parametrización del formulario de pacientes"
    size="full"
    :button="false"
  >
    <template #modal-body>
      <div class="space-y-4">
        <AlertDefault
          titulo="Configuración de campos"
          texto="Define qué datos se solicitan al registrar un paciente. Los campos de identificación se mantienen visibles para poder validar el documento. En los selectores puedes escoger la opción que se usará por defecto."
          color="blue"
          icono="InformationCircleIcon"
        />

        <div
          class="max-h-[65vh] overflow-auto rounded-lg border border-gray-300 dark:border-gray-700"
        >
          <table
            class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
          >
            <thead class="sticky top-0 z-10 bg-slate-100 dark:bg-slate-800">
              <tr>
                <th
                  class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300"
                >
                  Campo
                </th>
                <th
                  class="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300"
                >
                  Requerido
                </th>
                <th
                  class="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300"
                >
                  Oculto
                </th>
                <th
                  class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300"
                >
                  Valor por defecto
                </th>
              </tr>
            </thead>
            <tbody
              class="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-slate-900"
            >
              <tr
                v-for="campo in camposPacienteConfigurables"
                :key="campo.clave"
              >
                <td
                  class="px-4 py-3 text-sm font-medium text-gray-800 dark:text-gray-100"
                >
                  {{ campo.etiqueta }}
                </td>
                <td class="px-4 py-3 text-center">
                  <SwitchDefault
                    :id="`paciente-requerido-${campo.clave}`"
                    v-model="campos[campo.clave].requerido"
                    :true-value="true"
                    :false-value="false"
                    :label="campos[campo.clave].requerido ? 'Sí' : 'No'"
                  />
                </td>
                <td class="px-4 py-3 text-center">
                  <SwitchDefault
                    :id="`paciente-oculto-${campo.clave}`"
                    v-model="campos[campo.clave].oculto"
                    :true-value="true"
                    :false-value="false"
                    :label="campos[campo.clave].oculto ? 'Sí' : 'No'"
                  />
                </td>
                <td class="px-4 py-3 min-w-64">
                  <SelectDefault
                    v-if="campo.usarSelectDefault"
                    :id="`paciente-defecto-${campo.clave}`"
                    v-model="campos[campo.clave].valorPorDefecto"
                    :options="obtenerOpciones(campo)"
                    option-text="optionText"
                    option-value="optionValue"
                    :label="`Valor por defecto para ${campo.etiqueta}`"
                  />
                  <SelectSearch
                    v-else-if="campo.usarSelectSearch"
                    :id="`paciente-defecto-${campo.clave}`"
                    v-model:search="busquedas[campo.clave]"
                    :value="campos[campo.clave].valorPorDefecto"
                    :list="obtenerListaOpciones(campo)"
                    :options="obtenerOpcionesBusqueda(campo)"
                    :initial-data="campo.opciones !== 'divipola'"
                    :clear="true"
                    placeholder="Buscar opción"
                    :label="`Valor por defecto para ${campo.etiqueta}`"
                    @update:value="
                      actualizarValorPorDefecto(campo.clave, $event)
                    "
                  />
                  <InputDefault
                    v-else
                    :id="`paciente-defecto-${campo.clave}`"
                    v-model="campos[campo.clave].valorPorDefecto"
                    :type="campo.tipoDato === 'fecha' ? 'date' : 'text'"
                    :placeholder="
                      campo.tipoDato === 'seleccion'
                        ? 'ID de la opción'
                        : 'Sin valor por defecto'
                    "
                    :label="`Valor por defecto para ${campo.etiqueta}`"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <template #modal-footer>
      <div class="flex w-full justify-end gap-2">
        <ButtonDefault @click="closeModal(MODAL_ID)">Cancelar</ButtonDefault>
        <ButtonDefault color="blue" :is-loading="guardando" @click="guardar">
          <MdiIcon icono="mdiContentSaveOutline" class="size-4" />
          Guardar configuración
        </ButtonDefault>
      </div>
    </template>
  </ModalDefault>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import MdiIcon from '@/modules/Core/components/general/MdiIcon.vue'
import { closeModal, openModal } from '@/modules/Core/usings/generales'
import type { IConfConfiguracion } from '@/modules/Configuracion/interfaces'
import type { IConfMaestra } from '@/modules/Configuracion/interfaces'
import type { IConfDivipola } from '@/modules/Configuracion/interfaces'
import type { ITipoGeneralesPersona } from '@/modules/Administracion/interfaces/IPacienteData'
import type { ISelectSearchOptionsProps } from '@/modules/Core/interfaces'
import {
  CATEGORIA_CONFIGURACION_PACIENTES,
  CODIGO_CONFIGURACION_PACIENTES,
  camposPacienteConfigurables,
  crearConfiguracionCamposPaciente,
  serializarConfiguracionCamposPaciente
} from '@/modules/Administracion/utils/pacientesConfiguracion'
import type { IPacientesConfiguracionCampos } from '@/modules/Administracion/interfaces/IPacientesConfiguracion'
import { configuracionServicios, divipolaService } from '@/services'

const MODAL_ID = 'modal-configuracion-pacientes'
type IPacienteConfiguracionOpcion = {
  optionText: string
  optionValue: string
}

const props = defineProps<{
  tipos: ITipoGeneralesPersona
}>()

const emit = defineEmits<{
  guardado: [campos: IPacientesConfiguracionCampos]
}>()

const campos = ref<IPacientesConfiguracionCampos>(
  crearConfiguracionCamposPaciente()
)
const busquedas = ref<Record<string, string>>({})
const registroConfiguracion = ref<IConfConfiguracion | null>(null)
const guardando = ref(false)

const obtenerOpciones = (
  campo: (typeof camposPacienteConfigurables)[number]
): IPacienteConfiguracionOpcion[] => {
  const opciones = campo.opciones ? props.tipos[campo.opciones] : []

  return [
    { optionText: 'Sin valor por defecto', optionValue: '' },
    ...opciones.map((opcion: IConfMaestra) => ({
      optionText: opcion.comaNombrelargo,
      optionValue:
        campo.clave === 'coppTipoEstado'
          ? opcion.comaNombrecorto
          : String(opcion.comaId ?? '')
    }))
  ]
}

const obtenerListaOpciones = (
  campo: (typeof camposPacienteConfigurables)[number]
): IConfMaestra[] => {
  if (!campo.opciones || campo.opciones === 'divipola') return []
  return props.tipos[campo.opciones]
}

const obtenerOpcionesBusqueda = (
  campo: (typeof camposPacienteConfigurables)[number]
): ISelectSearchOptionsProps => {
  if (campo.opciones === 'divipola') {
    const obtenerNombreDivipola = (item: object) => {
      const divipola = item as IConfDivipola
      return [divipola.nomPais, divipola.nomDepartamento, divipola.nomMunicipio]
        .filter(Boolean)
        .join('-')
    }

    return {
      urlSearch: 'divipolaService',
      action: 'searchByName',
      fieldFilter: {
        name: 'nomPais',
        mapOptions: obtenerNombreDivipola
      },
      mapOptions: (item: object) => ({
        optionText: obtenerNombreDivipola(item),
        optionValue: (item as IConfDivipola).codiId
      })
    }
  }

  return {
    fieldFilter: {
      name: 'comaNombrelargo',
      mapOptions: (item: object) => (item as IConfMaestra).comaNombrelargo
    },
    mapOptions: (item: object) => ({
      optionText: (item as IConfMaestra).comaNombrelargo,
      optionValue: (item as IConfMaestra).comaId ?? ''
    })
  }
}

const actualizarValorPorDefecto = (
  campo: (typeof camposPacienteConfigurables)[number]['clave'],
  valor: unknown
) => {
  campos.value[campo].valorPorDefecto = String(valor ?? '')
}

const cargarBusquedaDivipola = async (
  campo: (typeof camposPacienteConfigurables)[number]
) => {
  const valor = campos.value[campo.clave].valorPorDefecto
  if (!valor || campo.opciones !== 'divipola') return

  try {
    const { data } = await divipolaService.view(Number(valor))
    const divipola = data.body
    if (!divipola) return

    busquedas.value[campo.clave] = [
      divipola.nomPais,
      divipola.nomDepartamento,
      divipola.nomMunicipio
    ]
      .filter(Boolean)
      .join('-')
  } catch {
    busquedas.value[campo.clave] = ''
  }
}

const open = (
  configuracion: IPacientesConfiguracionCampos,
  registro: IConfConfiguracion | null
) => {
  campos.value = JSON.parse(
    JSON.stringify(configuracion)
  ) as IPacientesConfiguracionCampos
  registroConfiguracion.value = registro
  busquedas.value = {}
  for (const campo of camposPacienteConfigurables) {
    if (!campo.usarSelectSearch) continue
    busquedas.value[campo.clave] = ''
    const valor = campos.value[campo.clave].valorPorDefecto
    if (!valor) continue

    if (campo.opciones === 'divipola') {
      void cargarBusquedaDivipola(campo)
      continue
    }

    const opcion = obtenerListaOpciones(campo).find(
      item => String(item.comaId) === valor
    )
    busquedas.value[campo.clave] = opcion?.comaNombrelargo ?? ''
  }
  openModal(MODAL_ID)
}

const guardar = async () => {
  guardando.value = true
  window.notificacion.loading().circle('Guardando parametrización de pacientes')

  try {
    const registro: IConfConfiguracion = {
      ...(registroConfiguracion.value?.cocoId
        ? { cocoId: registroConfiguracion.value.cocoId }
        : {}),
      cocoCodigo: CODIGO_CONFIGURACION_PACIENTES,
      cocoLabel: 'Configuración de campos del formulario de pacientes',
      cocoValue: serializarConfiguracionCamposPaciente(campos.value),
      cocoCategoria: CATEGORIA_CONFIGURACION_PACIENTES,
      cocoTipo: 'JSON'
    }
    const { data } = await configuracionServicios.save([registro])

    if (!data.body) {
      window.notificacion.notificacion(
        'failure',
        'No se pudo guardar la configuración de pacientes'
      )
      return
    }

    emit(
      'guardado',
      JSON.parse(JSON.stringify(campos.value)) as IPacientesConfiguracionCampos
    )
    window.notificacion.notificacion(
      'success',
      'Configuración de pacientes guardada'
    )
    closeModal(MODAL_ID)
  } catch {
    // configuracionServicios ya informa los errores de la solicitud.
  } finally {
    guardando.value = false
    window.notificacion.loading().remove()
  }
}

defineExpose({ open })
</script>
